import { type ObservabilityTool } from 'components/status-checks/authentication-form-utils'
import {
  TeamExternalIntegrationAuthenticationStatus,
  type ObservabilityToolType,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import { appdynamicsBaseUrlRegex } from 'utils/status-checks/status-check-integrations-util-appdynamics'
import { dynatraceBaseUrlRegex } from 'utils/status-checks/status-check-integrations-util-dynatrace'
import { grafanaBaseUrlRegex } from 'utils/status-checks/status-check-integrations-util-grafana'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import { allowedIntegrationURLs } from 'utils/yup-additions'

// Assert that a TeamExternalIntegration instance is of a specific IntegrationType
export function isExternalIntegrationOfType<
  TExternalIntegrationType extends IntegrationType
>(
  type: TExternalIntegrationType,
  externalIntegration: TeamExternalIntegration | undefined
): externalIntegration is TeamExternalIntegration & {
  type: TExternalIntegrationType
} {
  return Boolean(externalIntegration?.type === type)
}

export function isExternalIntegrationAuthenticated(
  externalIntegration: TeamExternalIntegration | undefined
) {
  return (
    Boolean(externalIntegration) &&
    externalIntegration?.lastAuthenticationStatus ===
      TeamExternalIntegrationAuthenticationStatus.Authenticated
  )
}

// Assert that an ObservabilityTool instance is of a specific ObservabilityToolType
export function isObservabilityToolOfType<
  TObservabilityToolType extends ObservabilityToolType
>(
  type: TObservabilityToolType,
  observabilityTool: ObservabilityTool | undefined
): observabilityTool is ObservabilityTool & { type: TObservabilityToolType } {
  return observabilityTool?.type === type
}

// Intended as a reducer accumulator function to extract AWS domains from an array of AWS external integrations
export function getAwsDomainsFromExternalIntegrations(
  externalIntegrations: TeamExternalIntegration[]
) {
  return externalIntegrations
    .filter(
      (externalIntegration) =>
        isExternalIntegrationOfType(IntegrationType.AWS, externalIntegration) &&
        externalIntegration.isConfigured
    )
    .map(({ domain }) => domain)
}

export function getNamesOfCustomExternalIntegrations(
  externalIntegrations: TeamExternalIntegration[]
) {
  return externalIntegrations
    .filter((externalIntegration) =>
      isExternalIntegrationOfType(IntegrationType.Custom, externalIntegration)
    )
    .map(({ name }) => name)
}

export function getAuthenticationUrlsFromExternalIntegration(
  externalIntegrations: TeamExternalIntegration[]
) {
  return externalIntegrations
    .filter(({ isConfigured, url }) => Boolean(isConfigured && url))
    .map(({ url }: Omit<TeamExternalIntegration, 'url'> & { url: string }) =>
      url.toLowerCase()
    )
}

export function isAuthenticationUrlUnique(
  urls: string[],
  value: string | undefined
) {
  const trimmedValue = value?.trim()
  return trimmedValue ? !urls.includes(trimmedValue) : false
}

export function validateApiBaseUrl(
  value: string | undefined,
  integrationType: IntegrationType,
  isPrivateNetworkIntegration: boolean
) {
  if (!value) {
    return false
  }

  try {
    new URL(value)
  } catch (e) {
    return false
  }

  if (integrationType === IntegrationType.Grafana) {
    if (isPrivateNetworkIntegration) {
      return true
    }
    return grafanaBaseUrlRegex.test(value)
  }

  if (integrationType === IntegrationType.Dynatrace) {
    return dynatraceBaseUrlRegex.test(value)
  }

  if (integrationType === IntegrationType.Appdynamics) {
    return appdynamicsBaseUrlRegex.test(value)
  }

  return Boolean(
    allowedIntegrationURLs[
      integrationType ||
        IntegrationType.Custom ||
        IntegrationType.LoadGeneratorCustom
    ].find((u) => u.test(value))
  )
}
